import { default as difference } from '@turf/difference';
import { polygon } from '@turf/helpers';
import { default as intersect } from '@turf/intersect';

import { buildGroups } from './helpers';
import { checkCollition } from './solarGeometryhelpers';

// Configuration

const STRIP_SPACING_M = 1; // Spacing between strips in meters
const RECT_SPACING_M = 0.02; // Spacing between rectangles in meters
const MIN_STEP_M = 0.1; // Smallest step for denser placement and strip shifting in meters
const SPACING_X = 0.02; // Spacing between solar modules in meters (horizontal)
const SPACING_Y = 0.02; // Spacing between solar modules in meters (

const METERS_PER_DEGREE_LAT = 111320; // Meters per degree latitude (approximate)

function rotatePolygon({ angle, google, polygon, center }) {
    const vertices = polygon.getPath();
    const newPath = [];
    const paths = [];

    for (let i = 0; i < vertices.getLength(); i++) {
        const vertex = vertices.getAt(i);
        paths.push({ lat: vertex.lat(), lng: vertex.lng() });
    }

    paths.forEach((_, i) => {
        const length = google.maps.geometry.spherical.computeLength([
            center,
            paths[i],
        ]);

        const heading = google.maps.geometry.spherical.computeHeading(
            center,
            paths[i],
        );

        const newPosition = google.maps.geometry.spherical.computeOffset(
            center,
            length,
            heading + angle,
        );

        newPath.push(newPosition);
    });

    polygon.setPath(newPath);
}

// Function to convert meters to degrees (approximate)
function metersToDegrees(meters, latitude, isLatitude) {
    const metersPerDegree = isLatitude
        ? METERS_PER_DEGREE_LAT
        : METERS_PER_DEGREE_LAT * Math.cos((latitude * Math.PI) / 180);
    return meters / metersPerDegree;
}

// Function to count rectangles in a sub-polygon
function countRectanglesInSubPolygon({
    minStepM = MIN_STEP_M,
    rectHeightsM,
    rectSpacingM = RECT_SPACING_M,
    rectWidthsM,
    subPolygonCoords,
}) {
    const bounds = subPolygonCoords.reduce(
        (b, coord) => ({
            minLat: Math.min(b.minLat, coord.lat),
            maxLat: Math.max(b.maxLat, coord.lat),
            minLng: Math.min(b.minLng, coord.lng),
            maxLng: Math.max(b.maxLng, coord.lng),
        }),
        {
            minLat: Infinity,
            maxLat: -Infinity,
            minLng: Infinity,
            maxLng: -Infinity,
        },
    );

    const rectSpacingDegLat = metersToDegrees(
        rectSpacingM,
        bounds.minLat,
        true,
    );
    const rectSpacingDegLng = metersToDegrees(
        rectSpacingM,
        bounds.minLat,
        false,
    );
    const minStepDegLat = metersToDegrees(minStepM, bounds.minLat, true);
    const minStepDegLng = metersToDegrees(minStepM, bounds.minLat, false);

    let stripPolygonPoints = subPolygonCoords.map((point) => [
        point.lng,
        point.lat,
    ]);
    stripPolygonPoints.push(stripPolygonPoints[0]);
    const stripPolygon = polygon([stripPolygonPoints]);

    let rectangleCount = 0;

    let currentLat = bounds.minLat;
    while (currentLat < bounds.maxLat) {
        let placedHeight = false;
        for (const heightM of rectHeightsM) {
            const heightDeg = metersToDegrees(
                heightM.size,
                bounds.minLat,
                true,
            );
            if (currentLat + heightDeg <= bounds.maxLat) {
                let currentLng = bounds.minLng;
                while (currentLng < bounds.maxLng) {
                    let placedWidth = false;
                    for (const widthM of rectWidthsM) {
                        const widthDeg = metersToDegrees(
                            widthM.size,
                            bounds.minLat,
                            false,
                        );
                        if (currentLng + widthDeg <= bounds.maxLng) {
                            const rectCoords = [
                                { lat: currentLat, lng: currentLng },
                                {
                                    lat: currentLat,
                                    lng: currentLng + widthDeg,
                                },
                                {
                                    lat: currentLat + heightDeg,
                                    lng: currentLng + widthDeg,
                                },
                                {
                                    lat: currentLat + heightDeg,
                                    lng: currentLng,
                                },
                            ];

                            let rectanglePolygonPoints = rectCoords.map(
                                (point) => [point.lng, point.lat],
                            );
                            rectanglePolygonPoints.push(
                                rectanglePolygonPoints[0],
                            );

                            const rectanglePolygon = polygon([
                                rectanglePolygonPoints,
                            ]);
                            const polyDifference = difference(
                                rectanglePolygon,
                                stripPolygon,
                            );

                            if (!polyDifference) {
                                rectangleCount++;
                                currentLng += widthDeg + rectSpacingDegLng;
                                placedWidth = true;
                                break;
                            }
                        }
                    }
                    if (!placedWidth) {
                        currentLng += minStepDegLng;
                    }
                }
                currentLat += heightDeg + rectSpacingDegLat;
                placedHeight = true;
                break;
            }
        }
        if (!placedHeight) {
            currentLat += minStepDegLat;
        }
    }

    return rectangleCount;
}

// Function to count total rectangles for a given starting offset
function countTotalRectanglesForOffset({
    google,
    polygon,
    rectHeightsM,
    rectSpacingM,
    rectWidthsM,
    startOffset,
    stripSizeM,
    stripSpacingM,
}) {
    const bounds = new google.maps.LatLngBounds();
    polygon.getPath().forEach((coord) => bounds.extend(coord));

    const west = bounds.getSouthWest().lng();
    const east = bounds.getNorthEast().lng();
    const south = bounds.getSouthWest().lat();
    const north = bounds.getNorthEast().lat();

    const mainPolygonGeoJSON = {
        type: 'Feature',
        geometry: {
            type: 'Polygon',
            coordinates: [
                [
                    ...polygon
                        .getPath()
                        .getArray()
                        .map((p) => [p.lng(), p.lat()]),
                    [
                        polygon.getPath().getAt(0).lng(),
                        polygon.getPath().getAt(0).lat(),
                    ],
                ],
            ],
        },
    };

    let totalRectangles = 0;

    const stripHeightDeg = metersToDegrees(stripSizeM, north, true);
    const stripSpacingDeg = metersToDegrees(stripSpacingM, north, true);
    const totalStripHeight = stripHeightDeg + stripSpacingDeg;

    let currentLat = south + startOffset;
    while (currentLat < north) {
        const stripSouth = currentLat;
        const stripNorth = Math.min(currentLat + stripHeightDeg, north);

        const stripCoords = [
            [west, stripSouth],
            [east, stripSouth],
            [east, stripNorth],
            [west, stripNorth],
            [west, stripSouth],
        ];

        const stripGeoJSON = {
            type: 'Feature',
            geometry: {
                type: 'Polygon',
                coordinates: [stripCoords],
            },
        };

        try {
            const intersection = intersect(mainPolygonGeoJSON, stripGeoJSON);
            if (intersection && intersection.geometry) {
                let intersectionCoordsArray = [];
                if (intersection.geometry.type === 'Polygon') {
                    intersectionCoordsArray = [
                        intersection.geometry.coordinates[0],
                    ];
                } else if (intersection.geometry.type === 'MultiPolygon') {
                    intersectionCoordsArray =
                        intersection.geometry.coordinates.map(
                            (poly) => poly[0],
                        );
                }

                intersectionCoordsArray.forEach((coords) => {
                    const googleCoords = coords.map((coord) => ({
                        lat: coord[1],
                        lng: coord[0],
                    }));
                    totalRectangles += countRectanglesInSubPolygon({
                        rectHeightsM,
                        rectSpacingM,
                        rectWidthsM,
                        subPolygonCoords: googleCoords,
                    });
                });
            }
        } catch (error) {
            console.warn('Intersection calculation failed for strip:', error);
        }

        currentLat += totalStripHeight;
    }

    return totalRectangles;
}

// Function to fill a sub-polygon with rectangles using Google Maps geometry
function fillSubPolygonWithRectangles({
    azimuth,
    boxes,
    cosTotalTilt,
    google,
    groups,
    initialGroup,
    map,
    maxSolarModules,
    minStepM = MIN_STEP_M,
    obstacles,
    orientation,
    originalCenter,
    rectHeightsM,
    rectSpacingM,
    rectWidthsM,
    solarModuleHeight,
    solarModuleWidth,
    spacingX,
    spacingY,
    subPolygonCoords,
}) {
    const bounds = subPolygonCoords.reduce(
        (b, coord) => ({
            minLat: Math.min(b.minLat, coord.lat),
            maxLat: Math.max(b.maxLat, coord.lat),
            minLng: Math.min(b.minLng, coord.lng),
            maxLng: Math.max(b.maxLng, coord.lng),
        }),
        {
            minLat: Infinity,
            maxLat: -Infinity,
            minLng: Infinity,
            maxLng: -Infinity,
        },
    );

    const rectSpacingDegLat = metersToDegrees(
        rectSpacingM,
        bounds.minLat,
        true,
    );
    const rectSpacingDegLng = metersToDegrees(
        rectSpacingM,
        bounds.minLat,
        false,
    );
    const minStepDegLat = metersToDegrees(minStepM, bounds.minLat, true);
    const minStepDegLng = metersToDegrees(minStepM, bounds.minLat, false);

    let stripPolygonPoints = subPolygonCoords.map((point) => [
        point.lng,
        point.lat,
    ]);
    stripPolygonPoints.push(stripPolygonPoints[0]);
    const stripPolygon = polygon([stripPolygonPoints]);
    let cellIndex = 0;

    if (orientation === 'horizontal') {
        let currentLat = bounds.minLat;
        while (currentLat < bounds.maxLat) {
            let placedHeight = false;
            for (const heightM of rectHeightsM) {
                const heightDeg = metersToDegrees(
                    heightM.size,
                    bounds.minLat,
                    true,
                );
                if (currentLat + heightDeg <= bounds.maxLat) {
                    let currentLng = bounds.minLng;

                    while (currentLng < bounds.maxLng) {
                        let placedWidth = false;
                        for (const widthM of rectWidthsM) {
                            const widthDeg = metersToDegrees(
                                widthM.size,
                                bounds.minLat,
                                false,
                            );
                            if (currentLng + widthDeg <= bounds.maxLng) {
                                const rectCoords = [
                                    { lat: currentLat, lng: currentLng },
                                    {
                                        lat: currentLat,
                                        lng: currentLng + widthDeg,
                                    },
                                    {
                                        lat: currentLat + heightDeg,
                                        lng: currentLng + widthDeg,
                                    },
                                    {
                                        lat: currentLat + heightDeg,
                                        lng: currentLng,
                                    },
                                ];

                                let rectanglePolygonPoints = rectCoords.map(
                                    (point) => [point.lng, point.lat],
                                );
                                rectanglePolygonPoints.push(
                                    rectanglePolygonPoints[0],
                                );

                                const rectanglePolygon = polygon([
                                    rectanglePolygonPoints,
                                ]);
                                const polyDifference = difference(
                                    rectanglePolygon,
                                    stripPolygon,
                                );

                                if (polyDifference) continue;

                                const groupPolygon = new google.maps.Polygon({
                                    paths: rectCoords,
                                });

                                const groupPaths = groupPolygon
                                    .getPath()
                                    .getArray();

                                const isInsideObstacle = checkCollition({
                                    azimuth,
                                    google,
                                    map,
                                    obstacles,
                                    originalCenter,
                                    paths: groupPaths,
                                });

                                if (isInsideObstacle) continue;

                                groups.push('group');

                                generateRectangleGrid({
                                    azimuth,
                                    boxes,
                                    cellIndex,
                                    cols: widthM.units,
                                    containingPolygon: rectanglePolygon,
                                    cosTotalTilt,
                                    google,
                                    groupIndex:
                                        initialGroup + groups.length - 1,
                                    height: solarModuleHeight,
                                    map,
                                    maxSolarModules,
                                    originalCenter,
                                    rows: heightM.units,
                                    spacingX,
                                    spacingY,
                                    width: solarModuleWidth,
                                });

                                cellIndex++;

                                currentLng += widthDeg + rectSpacingDegLng;
                                placedWidth = true;
                                break;
                            }
                        }
                        if (!placedWidth) {
                            currentLng += minStepDegLng;
                        }
                    }
                    currentLat += heightDeg + rectSpacingDegLat;
                    placedHeight = true;
                    break;
                }
            }
            if (!placedHeight) {
                currentLat += minStepDegLat;
            }
        }
    } else if (orientation === 'vertical') {
        let currentLng = bounds.minLng;
        while (currentLng < bounds.maxLng) {
            let placedWidth = false;
            for (const widthM of rectWidthsM) {
                const widthDeg = metersToDegrees(
                    widthM.size,
                    bounds.minLat,
                    false,
                );
                if (currentLng + widthDeg <= bounds.maxLng) {
                    let currentLat = bounds.minLat;
                    while (currentLat < bounds.maxLat) {
                        let placedHeight = false;
                        for (const heightM of rectHeightsM) {
                            const heightDeg = metersToDegrees(
                                heightM.size,
                                bounds.minLat,
                                true,
                            );
                            if (currentLat + heightDeg <= bounds.maxLat) {
                                const rectCoords = [
                                    { lat: currentLat, lng: currentLng },
                                    {
                                        lat: currentLat,
                                        lng: currentLng + widthDeg,
                                    },
                                    {
                                        lat: currentLat + heightDeg,
                                        lng: currentLng + widthDeg,
                                    },
                                    {
                                        lat: currentLat + heightDeg,
                                        lng: currentLng,
                                    },
                                ];

                                let rectanglePolygonPoints = rectCoords.map(
                                    (point) => [point.lng, point.lat],
                                );
                                rectanglePolygonPoints.push(
                                    rectanglePolygonPoints[0],
                                );

                                const rectanglePolygon = polygon([
                                    rectanglePolygonPoints,
                                ]);
                                const polyDifference = difference(
                                    rectanglePolygon,
                                    stripPolygon,
                                );

                                if (!polyDifference) {
                                    generateRectangleGrid({
                                        azimuth,
                                        cols: widthM.units,
                                        containingPolygon: rectanglePolygon,
                                        cosTotalTilt,
                                        google,
                                        height: solarModuleHeight,
                                        map,
                                        originalCenter,
                                        rows: heightM.units,
                                        spacingX,
                                        spacingY,
                                        width: solarModuleWidth,
                                    });

                                    currentLat += heightDeg + rectSpacingDegLat;
                                    placedHeight = true;
                                    break;
                                }
                            }
                        }
                        if (!placedHeight) {
                            currentLat += minStepDegLat;
                        }
                    }
                    currentLng += widthDeg + rectSpacingDegLng;
                    placedWidth = true;
                    break;
                }
            }
            if (!placedWidth) {
                currentLng += minStepDegLng;
            }
        }
    }
}

function getGroupSize({
    cols,
    cosTotalTilt,
    height,
    rows,
    spacingX,
    spacingY,
    width,
}) {
    const totalWidth = cols * width + (cols - 1) * spacingX;
    const totalHeight = rows * (height * cosTotalTilt) + (rows - 1) * spacingY;

    return { height: totalHeight, width: totalWidth };
}

function generateRectangleGrid({
    azimuth,
    boxes,
    cellIndex,
    cols,
    containingPolygon,
    cosTotalTilt,
    google,
    groupIndex,
    height,
    maxSolarModules,
    originalCenter,
    rows,
    spacingX,
    spacingY,
    width,
}) {
    // Extract center latitude for longitude correction (use midpoint of polygon)
    const coords = containingPolygon.geometry.coordinates[0];
    const centerLat = (coords[0][1] + coords[2][1]) / 2; // Average of bottom-left and top-right latitudes

    // Meter-to-degree conversion
    const metersPerDegreeLat = 111320; // Meters per degree latitude (approximate)
    const metersPerDegreeLng =
        metersPerDegreeLat * Math.cos((centerLat * Math.PI) / 180); // Adjust for longitude

    const widthDeg = width / metersPerDegreeLng;
    const heightDeg = (height * cosTotalTilt) / metersPerDegreeLat;
    const spacingXDeg = spacingX / metersPerDegreeLng;
    const spacingYDeg = spacingY / metersPerDegreeLat;

    // Extract bottom-left corner of the containing polygon for grid generation
    const bottomLeftLng = containingPolygon.geometry.coordinates[0][0][0];
    const bottomLeftLat = containingPolygon.geometry.coordinates[0][0][1];

    let innerCount = 0;

    // Generate grid of rectangles within the containing polygon
    for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
            const rectBottomLeftLat =
                bottomLeftLat + row * (heightDeg + spacingYDeg);
            const rectBottomLeftLng =
                bottomLeftLng + col * (widthDeg + spacingXDeg);

            // Create a Turf.js rectangle (bounding box)
            const bbox = [
                rectBottomLeftLng,
                rectBottomLeftLat,
                rectBottomLeftLng + widthDeg,
                rectBottomLeftLat + heightDeg,
            ];

            // Generate a Turf.js polygon from the bounding box
            const turfPolygon = polygon([
                [
                    [bbox[0], bbox[1]], // bottom-left
                    [bbox[2], bbox[1]], // bottom-right
                    [bbox[2], bbox[3]], // top-right
                    [bbox[0], bbox[3]], // top-left
                    [bbox[0], bbox[1]], // close the loop
                ],
            ]);

            const paths = turfPolygon.geometry.coordinates[0].map((coord) => ({
                lat: coord[1],
                lng: coord[0],
            }));

            const solarModulePolygon = new google.maps.Polygon({
                paths: [paths[0], paths[1], paths[2], paths[3]],
            });

            rotatePolygon({
                angle: 180 + azimuth,
                center: originalCenter,
                google,
                polygon: solarModulePolygon,
            });

            const enable = boxes.length < maxSolarModules;
            const solarModulePolygonPaths = solarModulePolygon
                .getPath()
                .getArray()
                .map((point) => [point.lat(), point.lng()]);
            const internalId = `${groupIndex}-${row}-${col}-${cellIndex}`;

            const data = {
                cell: 0,
                col,
                enable,
                group: groupIndex,
                internalId,
                paths: solarModulePolygonPaths,
                row,
            };

            boxes.push(data);
            innerCount++;
        }
    }

    return { count: innerCount };
}

// Function to create strips (horizontal or vertical) and their intersections
export function createStripIntersections({
    azimuth,
    cosTotalTilt = 1,
    google,
    groupCols,
    groupRows,
    initialGroup,
    map,
    maxSolarModules,
    minCols,
    minRows,
    minStepM = MIN_STEP_M,
    obstacles,
    originalCenter,
    polygon,
    rectSpacingM = RECT_SPACING_M,
    solarModuleHeight,
    solarModuleWidth,
    spacingX = SPACING_X,
    spacingY = SPACING_Y,
    stripSpacingM = STRIP_SPACING_M,
}) {
    // Get the bounds of the polygon
    const bounds = new google.maps.LatLngBounds();
    polygon.getPath().forEach((coord) => bounds.extend(coord));

    const west = bounds.getSouthWest().lng();
    const east = bounds.getNorthEast().lng();
    const south = bounds.getSouthWest().lat();
    const north = bounds.getNorthEast().lat();

    // Convert main polygon to GeoJSON for Turf.js
    const mainPolygonGeoJSON = {
        type: 'Feature',
        geometry: {
            type: 'Polygon',
            coordinates: [
                [
                    ...polygon
                        .getPath()
                        .getArray()
                        .map((p) => [p.lng(), p.lat()]),
                    [
                        polygon.getPath().getAt(0).lng(),
                        polygon.getPath().getAt(0).lat(),
                    ],
                ],
            ],
        },
    };

    const { height: stripSizeM } = getGroupSize({
        cols: groupCols,
        cosTotalTilt,
        height: solarModuleHeight,
        rows: groupRows,
        spacingX,
        spacingY,
        width: solarModuleWidth,
    });

    const rectHeightsM = Array.from({ length: groupRows }, (_, index) => {
        const units = minRows + index;
        const { height } = getGroupSize({
            cols: groupCols,
            cosTotalTilt,
            height: solarModuleHeight,
            rows: units,
            spacingX,
            spacingY,
            width: solarModuleWidth,
        });
        return { size: height, units };
    }).sort((a, b) => b.units - a.units);

    const rectWidthsM = Array.from({ length: groupCols }, (_, index) => {
        const units = minCols + index;
        const { width } = getGroupSize({
            cols: units,
            cosTotalTilt,
            height: solarModuleHeight,
            rows: groupRows,
            spacingX,
            spacingY,
            width: solarModuleWidth,
        });
        return { size: width, units };
    }).sort((a, b) => b.units - a.units);

    const maxOffsetSteps = Math.ceil(stripSizeM / solarModuleHeight) + 1;

    // Find the optimal offset
    const minStepDeg = metersToDegrees(minStepM, north, true);
    const maxOffset = metersToDegrees(stripSizeM + stripSpacingM, north, true);
    const boxes = new Array();
    let bestOffset = 0;
    let maxRectangles = -1;
    let groups = [];

    for (
        let offset = 0;
        offset <= maxOffset && offset / minStepDeg < maxOffsetSteps;
        offset += minStepDeg
    ) {
        const totalRectangles = countTotalRectanglesForOffset({
            google,
            polygon,
            rectHeightsM,
            rectSpacingM,
            rectWidthsM,
            startOffset: offset,
            stripSizeM,
            stripSpacingM,
        });

        if (totalRectangles > maxRectangles) {
            maxRectangles = totalRectangles;
            bestOffset = offset;
        }
    }

    const stripHeightDeg = metersToDegrees(stripSizeM, north, true);
    const stripSpacingDeg = metersToDegrees(stripSpacingM, north, true);
    const totalStripHeight = stripHeightDeg + stripSpacingDeg;

    let currentLat = south + bestOffset;
    while (currentLat < north) {
        const stripSouth = currentLat;
        const stripNorth = Math.min(currentLat + stripHeightDeg, north);

        const stripCoords = [
            [west, stripSouth],
            [east, stripSouth],
            [east, stripNorth],
            [west, stripNorth],
            [west, stripSouth],
        ];

        const stripGeoJSON = {
            type: 'Feature',
            geometry: {
                type: 'Polygon',
                coordinates: [stripCoords],
            },
        };

        try {
            const intersection = intersect(mainPolygonGeoJSON, stripGeoJSON);
            if (intersection && intersection.geometry) {
                let intersectionCoordsArray = [];
                if (intersection.geometry.type === 'Polygon') {
                    intersectionCoordsArray = [
                        intersection.geometry.coordinates[0],
                    ];
                } else if (intersection.geometry.type === 'MultiPolygon') {
                    intersectionCoordsArray =
                        intersection.geometry.coordinates.map(
                            (poly) => poly[0],
                        );
                }

                intersectionCoordsArray.forEach((coords) => {
                    const googleCoords = coords.map((coord) => ({
                        lat: coord[1],
                        lng: coord[0],
                    }));

                    fillSubPolygonWithRectangles({
                        azimuth,
                        boxes,
                        cosTotalTilt,
                        google,
                        groups,
                        initialGroup,
                        map,
                        maxSolarModules,
                        obstacles,
                        orientation: 'horizontal',
                        originalCenter,
                        rectHeightsM,
                        rectSpacingM,
                        rectWidthsM,
                        solarModuleHeight,
                        solarModuleWidth,
                        spacingX,
                        spacingY,
                        subPolygonCoords: googleCoords,
                    });
                });
            }
        } catch (error) {
            console.warn('Intersection calculation failed for strip:', error);
        }

        currentLat += totalStripHeight;
    }

    return {
        boxes,
        grouped: buildGroups(boxes).filter(function (element) {
            return element !== undefined;
        }),
    };
}
